import React from 'react';
import BarChart from './BarChart';
import { duration, fmt, formatDateTime, jobKindLabel, kr, pct, typeLabel } from './adminFormat';

const sortedEntries = (obj) => Object.entries(obj || {}).sort((a, b) => b[1] - a[1]);

const STAT_LABELS = [
    ['seen', 'seen'], ['regso_covered', 'RegSO areas'], ['new', 'new'], ['updated', 'updated'], ['removed_missing', 'removed'],
    ['removed', 'old rows removed'], ['months_complete', 'months complete'], ['enriched', 'enriched'], ['requests', 'requests'], ['cloudflare_blocked', 'Cloudflare blocks'],
];

export const jobStatsLine = (stats) => {
    if (!stats || !Object.keys(stats).length) return '—';
    return STAT_LABELS
        .filter(([key]) => Number.isFinite(stats[key]))
        .map(([key, label]) => `${fmt(stats[key])} ${label}`)
        .join(' · ');
};

const OverviewTab = ({ overview, charts, onGoto }) => {
    if (!overview) return <p className="ax-muted">Loading…</p>;
    const o = overview;
    if (!o.total) {
        return (
            <div className="ax-empty">
                <h2>No listings yet</h2>
                <p>
                    The active listings table is empty. Start a scrape on the{' '}
                    <button type="button" className="ax-link" onClick={() => onGoto('jobs')}>Jobs</button> tab.
                </p>
            </div>
        );
    }

    const tiles = [
        ['Listings', fmt(o.total), `${fmt(o.kommuner)} kommuner`],
        ['Median asking price', kr(o.median_price), `${fmt(o.priced)} with a price`],
        ['Median price / m²', kr(o.median_sqm_price), 'asking price'],
        ['Average living area', o.avg_living_m2 ? `${o.avg_living_m2} m²` : '—', `${o.avg_rooms || '—'} rooms on average`],
        ['Upcoming (Snart till salu)', fmt(o.upcoming), pct(o.upcoming, o.total)],
        ['Bidding in progress', fmt(o.bidding), pct(o.bidding, o.total)],
        ['On the map (RegSO)', pct(o.with_regso, o.total), `${fmt(o.with_coords)} with coordinates`],
        ['Images', fmt(o.images), `${fmt(o.with_floorplan)} listings with floor plan`],
    ];
    const types = sortedEntries(charts?.by_type);
    const status = sortedEntries(charts?.status);
    const fill = sortedEntries(charts?.fill);
    const job = o.last_job;
    const jobStatus = o.job_status || {};

    return (
        <>
            <div className="ax-tiles">
                {tiles.map(([label, value, sub]) => (
                    <div key={label} className="ax-tile">
                        <div className="ax-tile-label">{label}</div>
                        <div className="ax-tile-value">{value}</div>
                        <div className="ax-tile-sub">{sub}</div>
                    </div>
                ))}
            </div>
            <div className="ax-grid ax-grid--two">
                <section className="ax-card">
                    <h3>Listings by property type</h3>
                    <BarChart horizontal size="short" labels={types.map(([k]) => typeLabel(k))} values={types.map(([, v]) => v)} />
                </section>
                <section className="ax-card">
                    <h3>Sale status</h3>
                    <BarChart horizontal size="short" labels={status.map(([k]) => k)} values={status.map(([, v]) => v)} />
                </section>
            </div>
            <div className="ax-grid ax-grid--two">
                <section className="ax-card">
                    <h3>Data completeness</h3>
                    <p className="ax-hint">
                        Share of listings with a value. Low price coverage is normal: upcoming sales (&quot;Snart till salu&quot;)
                        have no price yet. BRF, energy class and estimate come from the scrape&apos;s extras step.
                    </p>
                    <BarChart
                        horizontal
                        labels={fill.map(([k]) => k)}
                        values={fill.map(([, v]) => v)}
                        max={100}
                        format={(v) => `${v}%`}
                    />
                </section>
                <section className="ax-card">
                    <h3>Last scrape job</h3>
                    <dl className="ax-kv">
                        {job ? (
                            <>
                                <dt>Job</dt><dd>{jobKindLabel(job)}</dd>
                                <dt>Status</dt><dd><span className={`ax-status ax-status--${job.status}`}>{job.status}</span></dd>
                                <dt>Started</dt><dd>{formatDateTime(job.started)}</dd>
                                <dt>Finished</dt><dd>{formatDateTime(job.finished)}</dd>
                                <dt>Duration</dt><dd>{duration(job.duration_s)}</dd>
                                <dt>Proxy</dt><dd>{job.proxy || 'off'}</dd>
                                <dt>Result</dt><dd>{jobStatsLine(job.stats)}</dd>
                                {job.error && (<><dt>Error</dt><dd className="ax-error">{job.error}</dd></>)}
                            </>
                        ) : (
                            <>
                                <dt>Job</dt><dd>No job started from the admin page yet</dd>
                                <dt>Last run</dt><dd>{formatDateTime(jobStatus.last_run_at)}</dd>
                                <dt>Last success</dt><dd>{formatDateTime(jobStatus.last_success_at)}</dd>
                            </>
                        )}
                        <dt>Newest listing</dt><dd>{o.newest || '—'}</dd>
                        <dt>Extras enriched</dt><dd>{o.has_extra ? `${fmt(o.enriched)} listings (${pct(o.enriched, o.total)})` : 'Not yet (no listingExtra table)'}</dd>
                    </dl>
                </section>
            </div>
        </>
    );
};

export default OverviewTab;
